import React, { Component } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";

import { connect } from "react-redux";
import { getSMSDetails } from "../../actions/sms";
import { editTemplate, postTemplate } from "../../actions/template";
import { isAdminPanelRole } from "../../auth/roles";
import { isImpersonating } from "../../lib/impersonation";
import { getFromLocalStorage } from "../../lib/utils";
import Select from "../../components/Select";
import DltTmdField from "../../components/DltTmdField";

class TemplateForm extends Component {
  // TMD routing internals are hidden from customers — platform-tier roles
  // only, and impersonation renders the customer view (same rule as the
  // sidebar/dashboard). State still carries dltTmdIdOverride so an edit
  // by a customer round-trips the existing value untouched.
  canSeeTmd =
    isAdminPanelRole((getFromLocalStorage() || {}).role) && !isImpersonating();

  constructor(props) {
    super(props);
    const { data } = props;
    this.state = {
      name: data ? data.name : "",
      text: data ? data.text : "",
      // defaultSenderId: preselect a header for a NEW template (Send SMS
      // opens this form for the header it has selected).
      senderId: data ? data.senderId : props.defaultSenderId || "",
      id: data ? data.id : "",
      dltTemplateId: data ? data.dltTemplateId : "",
      // Rare per-template downstream TMD override — undefined-safe: the
      // backend field is being added in parallel and may not echo yet.
      dltTmdIdOverride: (data && data.dltTmdIdOverride) || "",
      smsDetails:
        props.smsDetails && props.smsDetails.success
          ? props.smsDetails.data
          : {},
      btnDisabled: false,
    };
    this.receivedSMSDetails = true;
    this.receivedPostTemplate = true;
    this.receivedEditTemplate = true;
  }
  componentDidMount() {
    if (!this.state.smsDetails.senderIds) {
      this.receivedSMSDetails = false;
      this.props.getSMSDetails();
    }
  }
  componentWillReceiveProps(nextProps) {
    const { smsDetails, postTemplateRes, editTemplateRes } = nextProps;
    if (this.receivedSMSDetails === false && smsDetails) {
      this.receivedSMSDetails = true;
      if (smsDetails.success) {
        const first = smsDetails.data.senderIds && smsDetails.data.senderIds[0];
        const firstVal = first ? first.value || first.id || first : "";
        // Preselect the tenant's DEFAULT header when one is set; fall back
        // to the first in the list.
        const preselect = smsDetails.data.defaultSenderId || firstVal;
        this.setState({
          smsDetails: smsDetails.data,
          senderId: this.state.senderId || preselect,
        });
      } else if (smsDetails.err) {
        alert(smsDetails.err.msg);
        window.location.reload();
      } else {
        alert("Something went wrong!");
        window.location.reload();
      }
    }
    if (this.receivedPostTemplate === false && postTemplateRes) {
      this.receivedPostTemplate = true;
      if (postTemplateRes.success) {
        alert("Template Added successfully!");
        this.props.handlePostTemplate();
      } else {
        const errMsg =
          postTemplateRes.err?.msg ||
          postTemplateRes.message ||
          "Failed to add template";
        alert(errMsg);
        this.setState({ btnDisabled: false });
      }
    }
    if (this.receivedEditTemplate === false && editTemplateRes) {
      this.receivedEditTemplate = true;
      if (editTemplateRes.success) {
        alert("Template Edited successfully!");
        this.props.handlePostTemplate();
      } else {
        const errMsg =
          editTemplateRes.err?.msg ||
          editTemplateRes.message ||
          "Failed to update template";
        alert(errMsg);
        this.setState({ btnDisabled: false });
      }
    }
  }
  handleInput = (id, name) => this.setState({ [id]: name });

  handleChange = (e) => {
    this.setState({ senderId: e.target.value });
  };

  handleSaveBtn = () => {
    const { senderId, id, dltTmdIdOverride } = this.state;
    const name = this.state.name.trim();
    const text = this.state.text.trim();
    const dltTemplateId = this.state.dltTemplateId.trim();
    if (!(name && text && senderId && dltTemplateId)) {
      alert("All fields are mandatory!");
      return;
    }
    // No DLT category / message class here — the backend derives the category
    // from the sender ID's header type.
    const payload = {
      templateUserName: name,
      content: text,
      header: senderId,
      dltTemplateId,
      dltTmdIdOverride: String(dltTmdIdOverride || "").trim(),
    };
    this.setState({ btnDisabled: true });
    if (this.props.data) {
      this.receivedEditTemplate = false;
      this.props.editTemplate(id, payload);
    } else {
      this.receivedPostTemplate = false;
      this.props.postTemplate({ ...payload, template_id: dltTemplateId });
    }
  };

  render() {
    const {
      name,
      senderId,
      smsDetails,
      dltTemplateId,
      text,
      btnDisabled,
      dltTmdIdOverride,
    } = this.state;
    const { closeHandler, data } = this.props;
    return (
      <div>
        <Dialog open onClose={closeHandler}>
          <DialogTitle>Add Template</DialogTitle>
          <DialogContent
            dividers
            sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
          >
            <Select
              id="selectedSenderId"
              value={senderId}
              onChange={this.handleChange}
              label="Select Header"
              sx={{ minWidth: window.isMobile() ? 300 : 400 }}
              options={smsDetails.senderIds}
              required
            />
            <TextField
              label="Template Name for Reference"
              size="small"
              fullWidth
              value={name}
              onChange={(e) => {
                this.handleInput("name", e.target.value);
              }}
              required
              variant="standard"
            />
            <TextField
              label="Template ID provided by DLT"
              size="small"
              fullWidth
              value={dltTemplateId}
              disabled={!!data}
              onChange={(e) => {
                this.handleInput("dltTemplateId", e.target.value);
              }}
              required
              variant="standard"
            />
            <TextField
              label="Template content exactly as per DLT"
              multiline
              rows={3}
              id="text"
              size="small"
              fullWidth
              value={text}
              onChange={(e) => {
                this.handleInput("text", e.target.value);
              }}
              required
              variant="standard"
            />
            {this.canSeeTmd && (
              <DltTmdField
                label="TMD override (rare)"
                value={dltTmdIdOverride}
                onChange={(v) => this.handleInput("dltTmdIdOverride", v)}
                helperText="Only when THIS template's chain is approved via a different downstream than its header."
                textFieldProps={{
                  size: "small",
                  fullWidth: true,
                  variant: "standard",
                  sx: { opacity: 0.8 },
                }}
              />
            )}
          </DialogContent>
          <DialogActions>
            <Button disabled={btnDisabled} onClick={closeHandler}>
              Cancel
            </Button>
            <Button
              disabled={btnDisabled}
              onClick={this.handleSaveBtn}
              variant="contained"
            >
              Save
            </Button>
            {btnDisabled && <CircularProgress size={20} thickness={2} />}
          </DialogActions>
        </Dialog>
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    smsDetails: state.smsResponse.smsDetailsResponse,
    postTemplateRes: state.template.postTemplate,
    editTemplateRes: state.template.editTemplate,
  };
}
export default connect(mapStateToProps, {
  getSMSDetails,
  postTemplate,
  editTemplate,
})(TemplateForm);
